import React from "react";
import { Spin } from "antd";
import {
  buildWecomAuthorizeUrlResult,
  clearOauthAttemptFlag,
  clearWecomOAuthFail,
  isLikelyWeComWebView,
  readWecomOAuthFail,
  rememberPathBeforeWecomOAuthRedirect,
} from "./wecomOAuth";
import {
  getMsOrgApiBaseUrl,
  resolveApplicationId,
  verifySessionWithMsOrg,
  type WecomMsOrgClientOptions,
} from "./wecomMsOrgApi";
import {
  clearWecomMsOrgToken,
  getWecomMsOrgToken,
  type WecomMsOrgTokenStorageMode,
} from "./tokenStorage";

const FullPageLoading: React.FC<{ text: string }> = ({ text }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "100vh",
      gap: 12,
    }}
  >
    <Spin size="large" />
    <span style={{ color: "#666" }}>{text}</span>
  </div>
);

export type WecomMsOrgDeniedContext = {
  detail: string | null;
  /** 清除短期失败标记并刷新到站点根路径 */
  clearFailAndReload: () => void;
};

export type WecomMsOrgAuthGuardProps = {
  children: React.ReactNode;
  /** localStorage / sessionStorage 中 token 的 key，默认 token */
  tokenKey?: string;
  /**
   * token 读写范围：local | session | both（默认 both，与历史 his 读取逻辑一致）
   */
  tokenStorage?: WecomMsOrgTokenStorageMode;
  /** 未通过校验且不会跳转 OAuth 时的自定义 UI；可为节点或 render prop */
  deniedFallback?:
    | React.ReactNode
    | ((ctx: WecomMsOrgDeniedContext) => React.ReactNode);
  /** 进入 denied 状态时回调一次（便于埋点） */
  onDenied?: (detail: string | null) => void;
} & WecomMsOrgClientOptions;

/**
 * 企微 WebView：无有效 JWT 时跳转 OAuth；有 token 则调 msOrg GET /auth/me 校验。
 */
export const WecomMsOrgAuthGuard: React.FC<WecomMsOrgAuthGuardProps> = ({
  children,
  tokenKey = "token",
  tokenStorage = "both",
  deniedFallback,
  onDenied,
  applicationId,
  msOrgApiBaseUrl,
  wecomRedirectUri,
  oauthStatePrefix,
  sessionVerifyTimeoutMs,
}) => {
  const [status, setStatus] = React.useState<"checking" | "ok" | "denied">("checking");
  const [deniedDetail, setDeniedDetail] = React.useState<string | null>(null);
  const onDeniedRef = React.useRef(onDenied);
  onDeniedRef.current = onDenied;

  const clientOpts = React.useMemo<WecomMsOrgClientOptions>(
    () => ({
      applicationId,
      msOrgApiBaseUrl,
      wecomRedirectUri,
      oauthStatePrefix,
      sessionVerifyTimeoutMs,
    }),
    [applicationId, msOrgApiBaseUrl, wecomRedirectUri, oauthStatePrefix, sessionVerifyTimeoutMs],
  );

  const clearFailAndReload = React.useCallback(() => {
    clearWecomOAuthFail();
    window.location.assign("/");
  }, []);

  React.useEffect(() => {
    const run = async () => {
      const fail = readWecomOAuthFail();
      if (fail) {
        setDeniedDetail(fail.message);
        setStatus("denied");
        onDeniedRef.current?.(fail.message);
        return;
      }

      const token = getWecomMsOrgToken(tokenKey, tokenStorage);
      if (token) {
        const session = await verifySessionWithMsOrg(token, clientOpts);
        if (session.kind === "ok") {
          clearOauthAttemptFlag();
          clearWecomOAuthFail();
          setStatus("ok");
          return;
        }
        if (session.kind === "transport_error") {
          setDeniedDetail(
            session.message
              ? `无法连接组织服务校验登录状态（${session.message}）。请检查网络后刷新，勿在未完成验证时自动换票进入。`
              : "无法连接组织服务校验登录状态。请检查网络后刷新。",
          );
          setStatus("denied");
          onDeniedRef.current?.(session.message ?? "transport_error");
          return;
        }
        clearWecomMsOrgToken(tokenKey, tokenStorage);
      }

      const oauthBuild = await buildWecomAuthorizeUrlResult(clientOpts);
      if (isLikelyWeComWebView() && oauthBuild.ok) {
        rememberPathBeforeWecomOAuthRedirect();
        window.location.replace(oauthBuild.url);
        return;
      }

      if (isLikelyWeComWebView() && !oauthBuild.ok) {
        const base = getMsOrgApiBaseUrl(clientOpts);
        const appIdForUrl = resolveApplicationId(clientOpts);
        const selfCheck =
          appIdForUrl.trim().length > 0
            ? ` 接口自查：${base}/public/wecom-oauth-client/${encodeURIComponent(appIdForUrl.trim())}`
            : "";
        setDeniedDetail(
          `无法发起企微授权跳转。msOrg：${oauthBuild.reason}` +
            (oauthBuild.code ? `（${oauthBuild.code}）` : "") +
            (oauthBuild.httpStatus != null && oauthBuild.httpStatus > 0
              ? ` [HTTP ${oauthBuild.httpStatus}]`
              : "") +
            `。请核对：① 组织后台该应用的「企微应用 id」已填且应用为启用；② msOrg 环境变量 WECOM_CORP_ID；③ 前端构建的 applicationId 与库中 applications.id 一致。` +
            selfCheck,
        );
      }

      setStatus("denied");
      onDeniedRef.current?.(null);
    };

    void run();
  }, [tokenKey, tokenStorage, clientOpts]);

  if (status === "checking") return <FullPageLoading text="正在校验企微登录状态..." />;
  if (status === "ok") return <>{children}</>;

  if (deniedFallback !== undefined && deniedFallback !== null) {
    const ctx: WecomMsOrgDeniedContext = { detail: deniedDetail, clearFailAndReload };
    if (typeof deniedFallback === "function") {
      return <>{(deniedFallback as (c: WecomMsOrgDeniedContext) => React.ReactNode)(ctx)}</>;
    }
    return <>{deniedFallback}</>;
  }

  return (
    <div style={{ padding: 24, textAlign: "center", color: "#666", maxWidth: 520, margin: "0 auto" }}>
      <p style={{ marginBottom: 16 }}>{deniedDetail || "未登录或登录已失效，请从企微工作台重新进入。"}</p>
      {deniedDetail ? (
        <button
          type="button"
          onClick={clearFailAndReload}
          style={{
            padding: "6px 16px",
            cursor: "pointer",
            border: "1px solid #d9d9d9",
            borderRadius: 6,
            background: "#fff",
          }}
        >
          清除提示并重试
        </button>
      ) : null}
    </div>
  );
};
